import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { useApi } from '../../hooks/useApi.js';
import { ApiError } from '../../lib/api.js';
import type { TicketTipoCatalogo } from '../tickets/types.js';
import type { CatalogoArea } from './types.js';

type CatalogoAgrupado = Record<string, TicketTipoCatalogo[]>;

interface TareasPorAreaCatalogoProps {
  onToast: (type: 'success' | 'error', message: string) => void;
  // Ver CatalogosPage.tsx — mismo criterio (canManageCatalogs) que Giro/
  // Origen/Plataforma/Tiers, no el específico de Áreas (Prompt Z).
  puedeGestionar?: boolean;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// Reubicado desde Tickets (antes solo backend, sin UI de administración
// propia) — misma tabla ticket_tipos_catalogo, mismas rutas /tickets/catalogo,
// ahora gestionado desde Catálogos junto al resto de catálogos de negocio.
// El Área es un <select> poblado desde el catálogo real de Áreas (Prompt AA,
// GET /catalogos/area) — ya no el enum estático AREAS. La cascada de Tipo de
// solicitud al crear ticket (NuevoTicketModal.tsx) no se toca.
export function TareasPorAreaCatalogo({ onToast, puedeGestionar = true }: TareasPorAreaCatalogoProps) {
  const { get, post, patch, delete: del } = useApi();
  const queryClient = useQueryClient();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [addArea, setAddArea] = useState('');
  const [addValue, setAddValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TicketTipoCatalogo | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tickets-catalogo-admin'],
    queryFn: () => get<CatalogoAgrupado>('/tickets/catalogo'),
  });

  // Mismo catálogo real que puebla el select de Área en Usuarios y en el
  // filtro de Tickets — solo activas (igual que cualquier <select> del
  // sistema, a diferencia de la vista de gestión de Áreas que sí ve inactivas).
  const { data: areasActivas } = useQuery({
    queryKey: ['catalogo-area'],
    queryFn: () => get<CatalogoArea[]>('/catalogos/area'),
  });
  const nombresAreas = (areasActivas ?? []).map(a => a.nombre);

  function invalidarTodo() {
    queryClient.invalidateQueries({ queryKey: ['tickets-catalogo-admin'] });
    // Prefijo compartido con el cascade de Tipo de solicitud (NuevoTicketModal.tsx,
    // queryKey ['tickets-catalogo', area]) — que refleje el cambio la próxima vez.
    queryClient.invalidateQueries({ queryKey: ['tickets-catalogo'] });
  }

  const crearMutation = useMutation({
    mutationFn: (payload: { area: string; tipo: string }) => post('/tickets/catalogo', payload),
    onSuccess: () => {
      invalidarTodo();
      setAddArea('');
      setAddValue('');
      onToast('success', 'Tarea agregada correctamente.');
    },
    onError: (err: unknown) => onToast('error', errorMessage(err, 'No fue posible agregar la tarea.')),
  });

  const editarMutation = useMutation({
    mutationFn: ({ id, tipo }: { id: string; tipo: string }) => patch(`/tickets/catalogo/${id}`, { tipo }),
    onSuccess: () => {
      invalidarTodo();
      setEditingId(null);
      onToast('success', 'Tarea actualizada correctamente.');
    },
    onError: (err: unknown) => onToast('error', errorMessage(err, 'No fue posible editar la tarea.')),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => patch(`/tickets/catalogo/${id}/toggle`),
    onSuccess: () => invalidarTodo(),
    onError: (err: unknown) => onToast('error', errorMessage(err, 'No fue posible cambiar el estatus de la tarea.')),
  });

  // Prompt: Ajustes finos, ítem 5 — eliminación física (además del toggle
  // activo/inactivo ya existente), bloqueada por el backend si hay tickets
  // que referencian esta tarea (mismo criterio que eliminarItem() de los
  // demás catálogos).
  const eliminarMutation = useMutation({
    mutationFn: (id: string) => del(`/tickets/catalogo/${id}`),
    onSuccess: () => {
      invalidarTodo();
      setDeleteTarget(null);
      onToast('success', 'Tarea eliminada correctamente.');
    },
    onError: (err: unknown) => {
      setDeleteTarget(null);
      onToast('error', errorMessage(err, 'No fue posible eliminar la tarea.'));
    },
  });

  function confirmarAgregar() {
    const tipo = addValue.trim();
    if (!tipo || !addArea) return;
    crearMutation.mutate({ area: addArea, tipo });
  }

  function iniciarEdicion(item: TicketTipoCatalogo) {
    setEditingId(item.id);
    setEditValue(item.tipo);
  }

  function confirmarEdicion() {
    const tipo = editValue.trim();
    if (!tipo || !editingId) return;
    editarMutation.mutate({ id: editingId, tipo });
  }

  // Áreas a mostrar como grupo: las activas del catálogo (para poder agregar
  // tareas nuevas) + cualquier área con tareas ya existentes aunque haya sido
  // desactivada/renombrada después (para no ocultar datos ya creados).
  const areasAMostrar = [...new Set([...nombresAreas, ...Object.keys(data ?? {})])];

  return (
    <div className="cat-card" style={{ gridColumn: '1 / -1' }}>
      <div className="cat-card-hdr">
        <h3>Tareas por Área</h3>
      </div>

      {puedeGestionar && (
        <div style={{ display: 'flex', gap: 10, padding: '14px 15px', flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: '1px solid #f1f5f9' }}>
          <div className="ff" style={{ minWidth: 170 }}>
            <label>Área</label>
            <select value={addArea} onChange={e => setAddArea(e.target.value)}>
              <option value="">Seleccionar…</option>
              {nombresAreas.map(area => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
          </div>
          <div className="ff" style={{ flex: 1, minWidth: 220 }}>
            <label>Nueva tarea</label>
            <input
              type="text"
              placeholder="Nombre de la tarea..."
              value={addValue}
              onChange={e => setAddValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') confirmarAgregar();
              }}
            />
          </div>
          <Button size="sm" onClick={confirmarAgregar} disabled={!addArea || !addValue.trim() || crearMutation.isPending}>
            + Agregar tarea
          </Button>
        </div>
      )}

      {isLoading && <div style={{ padding: 14, fontSize: 13, color: 'var(--muted)' }}>Cargando…</div>}

      {!isLoading &&
        areasAMostrar.map(area => {
          const tareas = data?.[area] ?? [];
          return (
            <div key={area} style={{ padding: '12px 15px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                {area}
              </div>
              {tareas.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>Sin tareas registradas.</div>
              ) : (
                <div>
                  {tareas.map(item => {
                    const editing = puedeGestionar && editingId === item.id;
                    return (
                      <div className="cat-item" key={item.id} style={item.activo ? undefined : { opacity: 0.55 }}>
                        {editing ? (
                          <div className="cat-item-edit-row">
                            <input
                              type="text"
                              value={editValue}
                              autoFocus
                              onChange={e => setEditValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') confirmarEdicion();
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                            />
                            <button className="cat-edit-action cat-edit-ok" title="Guardar" onClick={confirmarEdicion} disabled={editarMutation.isPending}>
                              ✓
                            </button>
                            <button className="cat-edit-action cat-edit-cancel-btn" title="Cancelar" onClick={() => setEditingId(null)}>
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="cat-item-edit-row">
                            <span className="cat-item-label">{item.tipo}</span>
                            {!item.activo && <Badge tone="neutral">Inactivo</Badge>}
                            {puedeGestionar && (
                              <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                                <button className="cat-action-btn" title="Editar" onClick={() => iniciarEdicion(item)}>
                                  ✎
                                </button>
                                <button className="cat-action-btn del" title="Eliminar" onClick={() => setDeleteTarget(item)}>
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <label className="tog" style={{ marginLeft: 10 }} title={item.activo ? 'Desactivar' : 'Activar'}>
                          <input
                            type="checkbox"
                            checked={item.activo}
                            disabled={!puedeGestionar}
                            onChange={() => toggleMutation.mutate(item.id)}
                          />
                          <span className="tog-slider" />
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

      <ConfirmModal
        open={deleteTarget !== null}
        title="¿Eliminar tarea?"
        body={deleteTarget ? `Se eliminará "${deleteTarget.tipo}" del área "${deleteTarget.area}". Esta acción no se puede deshacer.` : ''}
        confirmLabel="Eliminar"
        danger
        onConfirm={() => deleteTarget && eliminarMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
